import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  Alert,
  CardActionArea,
  TextField,
  CircularProgress,
  Stack,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Receipt as ClaimIcon,
  Edit as EditIcon,
  ErrorOutline as WrongCodesIcon,
  Schedule as NotReadyIcon,
  Settings as SettingsIcon,
  Feedback as FeedbackIcon,
  CheckCircle as CheckIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'motion/react';
import {
  RejectionModalContentProps,
  RejectionReason,
  GenerateClaimPayload,
  CPTCode,
  ICD10Code,
  Modifier,
} from '../../types';

// Step definitions for the multi-step flow
type ModalStep = 'initial' | 'edit' | 'confirmation' | 'feedback';

/**
 * Generate claim-specific rejection modal content component
 * Provides options specific to billing code rejections
 */
export const GenerateClaimRejection: React.FC<RejectionModalContentProps> = ({
  context,
  onReject,
  onCancel,
  onAccept,
  isLoading = false,
}) => {
  const { action, patientName } = context;
  const [currentStep, setCurrentStep] = useState<ModalStep>('initial');
  const [feedbackText, setFeedbackText] = useState('');
  const [autoCloseCountdown, setAutoCloseCountdown] = useState<number | null>(null);
  const [editedCptCodes, setEditedCptCodes] = useState<CPTCode[]>([]);
  const rejectionTriggeredRef = useRef(false);

  // Type guard to ensure we have the correct action data
  if (action.action_type !== 'billing_codes') {
    console.error('GenerateClaimRejection received wrong action type');
    return null;
  }

  const actionData = action.action_payload as GenerateClaimPayload;

  // Initialize edited codes
  useEffect(() => {
    setEditedCptCodes(actionData.cpt_codes);
  }, [actionData.cpt_codes]);

  // Auto-close countdown for confirmation step
  useEffect(() => {
    if (currentStep === 'confirmation' && autoCloseCountdown === null) {
      setAutoCloseCountdown(5);
    }
  }, [currentStep, autoCloseCountdown]);

  useEffect(() => {
    if (autoCloseCountdown !== null && autoCloseCountdown > 0) {
      const timer = setTimeout(() => {
        setAutoCloseCountdown(autoCloseCountdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (autoCloseCountdown === 0 && !rejectionTriggeredRef.current) {
      // When countdown reaches 0, trigger rejection
      rejectionTriggeredRef.current = true;
      onReject(RejectionReason.DONT_CARE_FOR_FEATURE);
    }
  }, [autoCloseCountdown, onReject]);

  const handleReasonSelect = (reason: RejectionReason) => {
    switch (reason) {
      case RejectionReason.WRONG_SUGGESTION:
        setCurrentStep('edit');
        break;
      case RejectionReason.DONT_CARE_FOR_FEATURE:
        setCurrentStep('confirmation');
        break;
      case RejectionReason.OTHER:
        setCurrentStep('feedback');
        break;
    }
  };

  const handleEditConfirm = () => {
    if (onAccept && editedCptCodes.length > 0) {
      const updatedPayload: GenerateClaimPayload = {
        cpt_codes: editedCptCodes,
        justification: actionData.justification,
      };
      onAccept(action.action_id, updatedPayload);
    }
  };

  const handleEditReject = () => {
    onReject(RejectionReason.WRONG_SUGGESTION);
  };

  // Helper functions for editing
  const updateEditedCPTCode = (index: number, field: keyof CPTCode, value: any) => {
    const newCodes = editedCptCodes.map((code, idx) => {
      if (idx === index) {
        return { ...code, [field]: value };
      }
      return code;
    });
    setEditedCptCodes(newCodes);
  };

  const addNewCPTCode = () => {
    setEditedCptCodes([
      ...editedCptCodes,
      {
        code: '',
        name: '',
        icd10_codes: [],
        modifiers: [],
      },
    ]);
  };

  const removeCPTCode = (index: number) => {
    setEditedCptCodes(editedCptCodes.filter((_, i) => i !== index));
  };

  const addICD10ToCode = (cptIndex: number) => {
    const newCodes = editedCptCodes.map((code, idx) => {
      if (idx === cptIndex) {
        return {
          ...code,
          icd10_codes: [...code.icd10_codes, { code: '', name: '' }],
        };
      }
      return code;
    });
    setEditedCptCodes(newCodes);
  };

  const removeICD10FromCode = (cptIndex: number, icd10Index: number) => {
    const newCodes = editedCptCodes.map((code, idx) => {
      if (idx === cptIndex) {
        return {
          ...code,
          icd10_codes: code.icd10_codes.filter((_, i) => i !== icd10Index),
        };
      }
      return code;
    });
    setEditedCptCodes(newCodes);
  };

  const updateICD10 = (
    cptIndex: number,
    icd10Index: number,
    field: keyof ICD10Code,
    value: string,
  ) => {
    const newCodes = editedCptCodes.map((code, idx) => {
      if (idx === cptIndex) {
        return {
          ...code,
          icd10_codes: code.icd10_codes.map((icd, icdIdx) => {
            if (icdIdx === icd10Index) {
              return { ...icd, [field]: value };
            }
            return icd;
          }),
        };
      }
      return code;
    });
    setEditedCptCodes(newCodes);
  };

  const handleFeedbackSubmit = () => {
    onReject(RejectionReason.OTHER, {
      feedback: feedbackText.trim() || undefined,
    });
  };

  const handleBack = () => {
    setCurrentStep('initial');
    setFeedbackText('');
    setAutoCloseCountdown(null);
    rejectionTriggeredRef.current = false;
  };

  // Get codes summary for display
  const cptCodes = actionData.cpt_codes;
  const allICD10Codes = cptCodes
    .flatMap(cpt => cpt.icd10_codes)
    .filter((icd, index, self) => index === self.findIndex(i => i.code === icd.code));

  return (
    <AnimatePresence mode="popLayout">
      <motion.div
        key={currentStep}
        layout
        layoutRoot
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{
          layout: { duration: 0.3, ease: 'easeInOut' },
          opacity: { duration: 0.1 },
        }}
        style={{ width: '100%' }}
      >
        {currentStep === 'initial' && (
          <>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
              {/* Action Summary */}
              <Alert severity="info" icon={<ClaimIcon fontSize="inherit" />}>
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 'bold', color: 'primary.dark' }}
                  >
                    Suggested claim generation:
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'primary.dark', mt: 0.5 }}>
                    {patientName && <>{patientName} • </>}
                    {cptCodes.length} CPT code{cptCodes.length !== 1 ? 's' : ''} and{' '}
                    {allICD10Codes.length} ICD-10 code{allICD10Codes.length !== 1 ? 's' : ''}
                  </Typography>
                </Box>
              </Alert>

              {/* Rejection Reason Options - Customized for billing codes */}
              <Card variant="outlined" sx={{ cursor: 'pointer' }}>
                <CardActionArea
                  onClick={() => handleReasonSelect(RejectionReason.WRONG_SUGGESTION)}
                  disabled={isLoading}
                  sx={{ p: 2 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <EditIcon color="primary" />
                    <Box>
                      <Typography variant="subtitle2" color="text.primary">
                        The billing codes are incorrect
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        The suggested billing codes don't match this encounter
                      </Typography>
                    </Box>
                  </Box>
                </CardActionArea>
              </Card>

              <Card variant="outlined" sx={{ cursor: 'pointer' }}>
                <CardActionArea
                  onClick={() => handleReasonSelect(RejectionReason.DONT_CARE_FOR_FEATURE)}
                  disabled={isLoading}
                  sx={{ p: 2 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <NotReadyIcon color="primary" />
                    <Box>
                      <Typography variant="subtitle2" color="text.primary">
                        Not ready to bill
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        I'm not ready to generate claims for this note yet.
                      </Typography>
                    </Box>
                  </Box>
                </CardActionArea>
              </Card>

              <Card variant="outlined" sx={{ cursor: 'pointer' }}>
                <CardActionArea
                  onClick={() => handleReasonSelect(RejectionReason.DONT_CARE_FOR_FEATURE)}
                  disabled={isLoading}
                  sx={{ p: 2 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <SettingsIcon color="primary" />
                    <Box>
                      <Typography variant="subtitle2" color="text.primary">
                        I don't want these suggestions
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Turn off claim generation suggestions for all future notes
                      </Typography>
                    </Box>
                  </Box>
                </CardActionArea>
              </Card>

              <Card variant="outlined" sx={{ cursor: 'pointer' }}>
                <CardActionArea
                  onClick={() => handleReasonSelect(RejectionReason.OTHER)}
                  disabled={isLoading}
                  sx={{ p: 2 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <FeedbackIcon color="primary" />
                    <Box>
                      <Typography variant="subtitle2" color="text.primary">
                        Other reason
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Something else prevented me from accepting this suggestion
                      </Typography>
                    </Box>
                  </Box>
                </CardActionArea>
              </Card>
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                pt: 2,
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              <Button onClick={onCancel} disabled={isLoading} variant="outlined">
                Cancel
              </Button>
            </Box>
          </>
        )}

        {currentStep === 'edit' && (
          <>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EditIcon color="primary" />
                <Typography variant="h6" color="text.primary">
                  Edit Billing Codes
                </Typography>
              </Box>

              <Alert severity="info">
                <Typography variant="body2">
                  Review and modify the billing codes below. You can remove codes that don't apply
                  and then choose to accept the corrected claim or reject it entirely.
                </Typography>
              </Alert>

              {/* Display editable CPT codes */}
              <Stack spacing={2}>
                {editedCptCodes.map((cptCode, cptIndex) => (
                  <Box
                    key={cptIndex}
                    sx={{
                      p: 2,
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      bgcolor: 'grey.50',
                    }}
                  >
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, minWidth: 40 }}>
                        CPT
                      </Typography>

                      <TextField
                        label="Code"
                        value={cptCode.code}
                        onChange={e => updateEditedCPTCode(cptIndex, 'code', e.target.value)}
                        size="small"
                        fullWidth
                      />

                      <Tooltip title="Remove this CPT code" arrow>
                        <IconButton
                          onClick={() => removeCPTCode(cptIndex)}
                          sx={{ ml: 1 }}
                          aria-label="remove CPT code"
                          color="primary"
                        >
                          <RemoveIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>

                    <TextField
                      label="Modifiers (comma separated codes)"
                      value={cptCode.modifiers.map(m => m.code).join(', ')}
                      onChange={e => {
                        const modifierCodes = e.target.value
                          .split(',')
                          .map(m => m.trim())
                          .filter(m => m);
                        const modifiers: Modifier[] = modifierCodes.map(code => ({
                          code,
                          name: '', // Name would need to be looked up or entered separately
                        }));
                        updateEditedCPTCode(cptIndex, 'modifiers', modifiers);
                      }}
                      size="small"
                      fullWidth
                      placeholder="e.g., 95, 25"
                      sx={{ mb: 2 }}
                    />

                    {/* ICD-10 codes for this CPT */}
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                      ICD-10 Codes:
                    </Typography>
                    {cptCode.icd10_codes.map((icd10, icd10Index) => (
                      <Stack
                        key={icd10Index}
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ mb: 1 }}
                      >
                        <TextField
                          label="Code"
                          value={icd10.code}
                          onChange={e => updateICD10(cptIndex, icd10Index, 'code', e.target.value)}
                          size="small"
                          fullWidth
                        />
                        <IconButton
                          onClick={() => removeICD10FromCode(cptIndex, icd10Index)}
                          size="small"
                          color="primary"
                        >
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    ))}
                    <Button
                      variant="text"
                      onClick={() => addICD10ToCode(cptIndex)}
                      startIcon={<AddIcon />}
                      size="small"
                      sx={{ mt: 1 }}
                    >
                      Add ICD-10
                    </Button>
                  </Box>
                ))}

                {editedCptCodes.length === 0 && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontStyle: 'italic', textAlign: 'center', py: 2 }}
                  >
                    No billing codes remaining. Click "Still Reject" to reject this suggestion.
                  </Typography>
                )}

                <Button
                  variant="outlined"
                  onClick={addNewCPTCode}
                  sx={{ alignSelf: 'flex-start', mt: 1 }}
                  startIcon={<AddIcon />}
                >
                  Add CPT Code
                </Button>
              </Stack>
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                pt: 2,
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              <Button onClick={handleBack} variant="outlined" color="inherit">
                Back
              </Button>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button onClick={handleEditReject} variant="outlined" color="error">
                  Still Reject
                </Button>
                <Button
                  onClick={handleEditConfirm}
                  variant="contained"
                  color="primary"
                  disabled={editedCptCodes.length === 0}
                >
                  Accept Changes
                </Button>
              </Box>
            </Box>
          </>
        )}

        {currentStep === 'confirmation' && (
          <>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                mb: 4,
                textAlign: 'center',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <CheckIcon sx={{ fontSize: 48, color: 'success.main' }} />
              </Box>

              <Typography variant="h6" color="text.primary">
                Thank You for Your Feedback
              </Typography>

              <Typography variant="body1" color="text.secondary">
                We appreciate you trying our experimental billing code suggestions. Your feedback
                helps us improve this feature for future releases.
              </Typography>

              {autoCloseCountdown !== null && autoCloseCountdown > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                  }}
                >
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Closing in {autoCloseCountdown} seconds...
                  </Typography>
                </Box>
              )}
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                pt: 2,
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              <Button
                onClick={() => {
                  if (!rejectionTriggeredRef.current) {
                    rejectionTriggeredRef.current = true;
                    onReject(RejectionReason.DONT_CARE_FOR_FEATURE);
                  }
                }}
                variant="contained"
                color="primary"
              >
                Close
              </Button>
            </Box>
          </>
        )}

        {currentStep === 'feedback' && (
          <>
            {/* Feedback step content */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <FeedbackIcon color="primary" />
                <Typography variant="h6" color="text.primary">
                  Tell us more
                </Typography>
              </Box>

              <Typography variant="body2" color="text.secondary">
                Help us improve our claim generation by sharing what prevented you from accepting
                these billing codes.
              </Typography>

              <TextField
                label="Your feedback"
                multiline
                rows={4}
                value={feedbackText}
                onChange={e => setFeedbackText(e.target.value)}
                placeholder="Please describe why these codes weren't helpful..."
                fullWidth
                variant="outlined"
              />
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                pt: 2,
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              <Button onClick={handleBack} variant="outlined" color="inherit">
                Back
              </Button>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  onClick={() => onReject(RejectionReason.OTHER)}
                  variant="outlined"
                  color="inherit"
                >
                  Skip
                </Button>
                <Button
                  onClick={handleFeedbackSubmit}
                  variant="contained"
                  color="primary"
                  disabled={!feedbackText.trim()}
                >
                  Submit Feedback
                </Button>
              </Box>
            </Box>
          </>
        )}
      </motion.div>
    </AnimatePresence>
  );
};
